import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';

import { IClient, Mode, ClientDatabase } from '~/types/client';
import { menuSessionExpiresAt, readMenuSession } from '~/utils/menuSession';

import styles from './MenuPlayersQR.module.css';

/** Sans session à montrer, le QR géant demandé par la télécommande s'explique puis se referme. */
const NO_SESSION_OVERLAY_MS = 6000;
/** Relecture de la session (la lib l'efface si le serveur la ferme pendant le menu). */
const RECHECK_MS = 15_000;

type MenuPlayersQRProps = {
    client: IClient;
};

/**
 * QR des joueurs en grand sur le menu de la TV, à la demande de la touche « QR » de la
 * télécommande (flag `screenInfos.anim.showQR`, comme pendant un jeu) : le QR de la
 * session de la soirée (les joueurs déjà là restent connectés, les nouveaux entrent).
 * Pas de session connue (1re arrivée, menu ouvert depuis trop longtemps) → une phrase
 * qui explique, refermée seule. Rien en permanence sur le menu (retiré le 29/10 :
 * menu trop chargé) ; à ne monter que pour un établissement réel (ni démo publique,
 * ni résilié, ni en pause).
 */
export function MenuPlayersQR({ client }: MenuPlayersQRProps) {
    const { t } = useTranslation();
    // Le drapeau n'est suivi qu'après l'avoir vu baissé depuis l'arrivée sur le menu : un
    // `showQR` resté levé du jeu précédent (remis à zéro par clearScreenGame) ne fait pas
    // clignoter le QR géant à l'arrivée.
    const rawFlag = client.getScreen(Mode.Anim)?.showQR ?? false;
    const [flagArmed, setFlagArmed] = useState(!rawFlag);
    useEffect(() => {
        if (!rawFlag) setFlagArmed(true);
    }, [rawFlag]);
    const remoteFlag = flagArmed && rawFlag;
    const remoteFlagRef = useRef(remoteFlag);
    remoteFlagRef.current = remoteFlag;
    // Fermé sur la TV alors que la télécommande l'a ouvert : reste fermé même si la remise
    // à zéro du flag échoue, jusqu'au prochain appui (flag redescendu puis relevé).
    const [remoteClosed, setRemoteClosed] = useState(false);
    const remoteClosedRef = useRef(false);
    remoteClosedRef.current = remoteClosed;
    useEffect(() => {
        if (!remoteFlag) setRemoteClosed(false);
    }, [remoteFlag]);

    // Sans session, le message se referme seul SANS baisser le drapeau (voir `close`) : la
    // tuile de la télécommande reste allumée et l'appui suivant baisse le drapeau. Cet
    // appui-là ré-affiche aussi le message, sinon il faudrait appuyer deux fois pour le revoir.
    const [lowerPress, setLowerPress] = useState(false);
    const prevFlagRef = useRef(rawFlag);
    useEffect(() => {
        const wasUp = prevFlagRef.current;
        prevFlagRef.current = rawFlag;
        if (wasUp && !rawFlag && remoteClosedRef.current && !readMenuSession(client.id)) setLowerPress(true);
    }, [rawFlag, client.id]);
    const big = (remoteFlag && !remoteClosed) || lowerPress;

    // Session de la soirée relue seulement QR ouvert, et à chaque rendu : le QR disparaît pile
    // quand on ne peut plus garantir la session (menuSession.ts), ou dès que la lib l'a
    // effacée (fermée par le serveur pendant le menu) — relecture toutes les 15 s.
    const session = big ? readMenuSession(client.id) : null;
    const sessionID = session?.sessionID ?? null;
    const url = session?.link ?? null;
    const [, setTick] = useState(0);
    useEffect(() => {
        if (!big || !sessionID) return undefined;
        const rerender = () => setTick((n) => n + 1);
        const at = menuSessionExpiresAt();
        const expiry = at ? window.setTimeout(rerender, Math.max(0, at - Date.now()) + 50) : undefined;
        const poll = window.setInterval(rerender, RECHECK_MS);
        return () => {
            window.clearTimeout(expiry);
            window.clearInterval(poll);
        };
    }, [big, sessionID]);

    // Remise à zéro du drapeau seulement si CE menu montrait vraiment le QR : un autre
    // écran du bar resté au menu sans session ne doit pas refermer le QR géant de la TV
    // qui joue (on ne peut pas savoir si elle joue : ce menu a remis `gameID` à null).
    const mayResetRef = useRef(false);
    mayResetRef.current = !!sessionID;
    const close = () => {
        setLowerPress(false);
        if (remoteFlagRef.current) setRemoteClosed(true);
        // Même resynchro que le QR géant en jeu : la télécommande ne doit pas croire le QR
        // encore affiché.
        if (remoteFlagRef.current && mayResetRef.current) {
            ClientDatabase.setScreenShowQR(client.id, Mode.Anim, false).catch((e) =>
                console.error('[menu] resync showQR=false failed', e)
            );
        }
    };
    const closeRef = useRef(close);
    closeRef.current = close;

    // Ce menu part en jeu après avoir refermé localement un QR sans session : le drapeau
    // encore levé ouvrirait le QR géant au début de SA partie → on le baisse alors.
    useEffect(
        () => () => {
            if (remoteClosedRef.current && remoteFlagRef.current) {
                ClientDatabase.setScreenShowQR(client.id, Mode.Anim, false).catch(() => {
                    /* best effort */
                });
            }
        },
        [client.id]
    );

    // Échap ferme le QR en grand (clavier de l'ordinateur branché à la TV).
    useEffect(() => {
        if (!big) return undefined;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') closeRef.current();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [big]);

    // Pas de session : le message se referme seul (la télécommande reflète l'état).
    useEffect(() => {
        if (!big || sessionID) return undefined;
        const id = window.setTimeout(() => closeRef.current(), NO_SESSION_OVERLAY_MS);
        return () => window.clearTimeout(id);
    }, [big, sessionID]);

    // QR géant : le focus va sur « Fermer » (un Entrée ne lance pas le jeu dessous) et
    // revient où il était à la fermeture.
    const closeBtnRef = useRef<HTMLButtonElement>(null);
    useEffect(() => {
        if (!big) return undefined;
        const before = document.activeElement as HTMLElement | null;
        closeBtnRef.current?.focus();
        return () => before?.focus?.();
    }, [big]);

    if (!big) return null;
    return (
        <div className={styles.scrim} role="dialog" aria-modal="true" aria-labelledby="menu-players-qr-title">
            <div className={styles.bigCard}>
                {url ? (
                    <>
                        <span className={styles.bigQr}>
                            <QRCodeSVG value={url} size={420} />
                        </span>
                        <span id="menu-players-qr-title" className={styles.bigTitle}>
                            {t('home.playersQR.title')}
                        </span>
                        <span className={styles.bigStay}>{t('home.playersQR.stay')}</span>
                    </>
                ) : (
                    <span id="menu-players-qr-title" className={styles.bigStay}>
                        {t('home.playersQR.later')}
                    </span>
                )}
                <button ref={closeBtnRef} type="button" className={styles.close} onClick={close}>
                    {t('home.playersQR.close')}
                </button>
            </div>
        </div>
    );
}
